import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowLeft, Loader2, Building2, Heart, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const License = () => {
  const [kind, setKind] = useState("venue"); // 'venue' | 'fundraiser'

  return (
    <div className="grain min-h-screen" data-testid="license-page">
      <div className="max-w-3xl mx-auto px-6 py-10">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-[#a39797] hover:text-[#d4af37] mb-8">
          <ArrowLeft size={16} /> back to the file
        </Link>

        <div className="mb-8">
          <div className="font-hand text-[#d4af37] rotate-[-1deg] text-lg mb-2">commercial license</div>
          <h1 className="font-display text-5xl text-[#f4f0ec] mb-3">Host a WasntMe.Party night.</h1>
          <p className="text-[#c7bebe] text-lg max-w-2xl">
            One-event licenses for venues and fundraisers. Pick a case, invite your crowd, sell tickets.
            We handle the mystery; you keep the door.
          </p>
        </div>

        {/* Kind toggle */}
        <div className="flex gap-2 mb-8" data-testid="license-kind-tabs">
          <button
            onClick={() => setKind("venue")}
            className={`card-noir p-5 flex-1 text-left transition-colors ${kind === "venue" ? "border-[#d4af37]" : ""}`}
            data-testid="license-kind-venue"
          >
            <Building2 size={16} className="text-[#d4af37] mb-2" />
            <div className="font-display text-xl text-[#f4f0ec]">Venue License</div>
            <div className="text-xs text-[#a39797] mt-1">Bar, restaurant, hotel. $299 per event.</div>
          </button>
          <button
            onClick={() => setKind("fundraiser")}
            className={`card-noir p-5 flex-1 text-left transition-colors ${kind === "fundraiser" ? "border-[#d4af37]" : ""}`}
            data-testid="license-kind-fundraiser"
          >
            <Heart size={16} className="text-[#d4af37] mb-2" />
            <div className="font-display text-xl text-[#f4f0ec]">Fundraiser License</div>
            <div className="text-xs text-[#a39797] mt-1">Nonprofit, booster, PTA. $249 / $449.</div>
          </button>
        </div>

        {kind === "venue" ? <VenueForm /> : <FundraiserForm />}

        <div className="card-noir p-5 mt-8 bg-[#1a1413]/50">
          <div className="text-xs tracking-widest text-[#a39797] mb-2">WHAT'S INCLUDED</div>
          <ul className="space-y-2 text-sm text-[#c7bebe]">
            <li className="flex gap-2"><CheckCircle2 size={14} className="text-[#4ade80] shrink-0 mt-0.5" />
              Commercial right to host one mystery night on your event date</li>
            <li className="flex gap-2"><CheckCircle2 size={14} className="text-[#4ade80] shrink-0 mt-0.5" />
              Pick any case from the Case Vault (including team editions)</li>
            <li className="flex gap-2"><CheckCircle2 size={14} className="text-[#4ade80] shrink-0 mt-0.5" />
              Marketing kit — poster PDF, Instagram caption pack, event copy</li>
            <li className="flex gap-2"><CheckCircle2 size={14} className="text-[#4ade80] shrink-0 mt-0.5" />
              15-minute "run of show" call with our team (optional)</li>
            <li className="flex gap-2"><CheckCircle2 size={14} className="text-[#4ade80] shrink-0 mt-0.5" />
              Email license certificate for your records</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

const VenueForm = () => {
  const [form, setForm] = useState({
    venue_name: "", contact_name: "", contact_email: "", event_date: "",
    expected_guests: 40, city: "", note: "",
  });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e?.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/license/venue/checkout", {
        ...form, expected_guests: parseInt(form.expected_guests, 10) || 1,
        origin_url: window.location.origin,
      });
      if (data.checkout_url) window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start checkout.");
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="card-noir p-6 space-y-3" data-testid="venue-form">
      <input required placeholder="Venue name" value={form.venue_name}
        onChange={(e) => setForm({ ...form, venue_name: e.target.value })}
        className="input-noir" data-testid="venue-name" />
      <div className="grid md:grid-cols-2 gap-3">
        <input required placeholder="Your name" value={form.contact_name}
          onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
          className="input-noir" data-testid="venue-contact-name" />
        <input required type="email" placeholder="Your email" value={form.contact_email}
          onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
          className="input-noir" data-testid="venue-contact-email" />
      </div>
      <div className="grid md:grid-cols-3 gap-3">
        <input required type="date" value={form.event_date}
          onChange={(e) => setForm({ ...form, event_date: e.target.value })}
          className="input-noir" data-testid="venue-event-date" />
        <input type="number" min="1" max="1000" placeholder="Expected guests" value={form.expected_guests}
          onChange={(e) => setForm({ ...form, expected_guests: e.target.value })}
          className="input-noir" data-testid="venue-guests" />
        <input placeholder="City" value={form.city}
          onChange={(e) => setForm({ ...form, city: e.target.value })}
          className="input-noir" data-testid="venue-city" />
      </div>
      <textarea placeholder="Note (optional) — theme, bar style, what night you need"
        value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })}
        maxLength={600} className="input-noir min-h-[80px]" data-testid="venue-note" />
      <button type="submit" disabled={busy} className="btn-primary w-full justify-center"
        data-testid="venue-submit">
        {busy ? <Loader2 size={14} className="animate-spin" /> : <Building2 size={14} />}
        Buy Venue License — $299
      </button>
    </form>
  );
};

const FundraiserForm = () => {
  const [form, setForm] = useState({
    org_name: "", tier: "standard", contact_name: "", contact_email: "",
    event_date: "", expected_guests: 50, cause: "", note: "",
  });
  const [busy, setBusy] = useState(false);
  const price = form.tier === "premium" ? "$449" : "$249";

  const submit = async (e) => {
    e?.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/license/fundraiser/checkout", {
        ...form, expected_guests: parseInt(form.expected_guests, 10) || 1,
        origin_url: window.location.origin,
      });
      if (data.checkout_url) window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start checkout.");
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="card-noir p-6 space-y-3" data-testid="fundraiser-form">
      <input required placeholder="Organization name" value={form.org_name}
        onChange={(e) => setForm({ ...form, org_name: e.target.value })}
        className="input-noir" data-testid="fundraiser-org" />
      <div className="grid grid-cols-2 gap-3">
        <button type="button" onClick={() => setForm({ ...form, tier: "standard" })}
          className={`card-noir p-3 text-left transition-colors ${form.tier === "standard" ? "border-[#d4af37]" : ""}`}
          data-testid="fundraiser-tier-standard">
          <div className="text-[#f4f0ec] font-bold">Standard · $249</div>
          <div className="text-xs text-[#a39797]">Up to 50 guests</div>
        </button>
        <button type="button" onClick={() => setForm({ ...form, tier: "premium" })}
          className={`card-noir p-3 text-left transition-colors ${form.tier === "premium" ? "border-[#d4af37]" : ""}`}
          data-testid="fundraiser-tier-premium">
          <div className="text-[#f4f0ec] font-bold">Premium · $449</div>
          <div className="text-xs text-[#a39797]">Up to 150 guests · branded intro</div>
        </button>
      </div>
      <div className="grid md:grid-cols-2 gap-3">
        <input required placeholder="Your name" value={form.contact_name}
          onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
          className="input-noir" data-testid="fundraiser-contact-name" />
        <input required type="email" placeholder="Your email" value={form.contact_email}
          onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
          className="input-noir" data-testid="fundraiser-contact-email" />
      </div>
      <div className="grid md:grid-cols-3 gap-3">
        <input required type="date" value={form.event_date}
          onChange={(e) => setForm({ ...form, event_date: e.target.value })}
          className="input-noir" data-testid="fundraiser-event-date" />
        <input type="number" min="1" max="1000" placeholder="Expected guests" value={form.expected_guests}
          onChange={(e) => setForm({ ...form, expected_guests: e.target.value })}
          className="input-noir" data-testid="fundraiser-guests" />
        <input placeholder="Cause (optional)" value={form.cause}
          onChange={(e) => setForm({ ...form, cause: e.target.value })}
          className="input-noir" data-testid="fundraiser-cause" />
      </div>
      <textarea placeholder="Note (optional)"
        value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })}
        maxLength={600} className="input-noir min-h-[80px]" data-testid="fundraiser-note" />
      <button type="submit" disabled={busy} className="btn-primary w-full justify-center"
        data-testid="fundraiser-submit">
        {busy ? <Loader2 size={14} className="animate-spin" /> : <Heart size={14} />}
        Buy Fundraiser License — {price}
      </button>
    </form>
  );
};

export const LicenseThanks = () => {
  const [params] = useSearchParams();
  const sid = params.get("session_id");
  const [state, setState] = useState("polling");

  useState(() => {
    if (!sid) { setState("missing"); return; }
    let tries = 0;
    const tick = async () => {
      try {
        const { data } = await api.get(`/license/thanks?session_id=${sid}`);
        if (data.paid) { setState("ready"); return; }
      } catch (err) { /* retry */ }
      tries += 1;
      if (tries < 20) setTimeout(tick, 1500);
      else setState("timeout");
    };
    tick();
  });

  return (
    <div className="grain min-h-screen flex items-center justify-center">
      <div className="card-noir p-8 max-w-md text-center" data-testid="license-thanks">
        {state === "polling" && <>
          <Loader2 size={28} className="mx-auto animate-spin text-[#d4af37] mb-3" />
          <div className="font-display text-xl text-[#f4f0ec] mb-2">Activating your license…</div>
          <div className="text-xs text-[#a39797]">Certificate email on the way.</div>
        </>}
        {state === "ready" && <>
          <CheckCircle2 size={32} className="mx-auto text-[#4ade80] mb-3" />
          <div className="font-display text-2xl text-[#f4f0ec] mb-2">You're licensed.</div>
          <div className="text-xs text-[#a39797] mb-4">Check your email for the certificate.</div>
          <Link to="/" className="btn-ghost text-sm">Back home</Link>
        </>}
      </div>
    </div>
  );
};

export default License;
